import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { AdminSettings, AppTheme } from '../services/admin-settings.model';
import { SettingsService } from '../services/admin-settings.service';
import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormField } from '@angular/material/form-field';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { TranslateModule } from '@ngx-translate/core';
import { CampaignService } from '../services/campaign.service';
import { Account } from '../account/account.model';
import { OnsiteCampaign, OnsiteCampaignType } from '../../campaign/campaign.model';
import { Router } from '@angular/router';
import { Environment } from '../../environment/environment.base';
import { ENVIRONMENT_DI } from '../../environment/environment';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { DarkMode, UserModel } from 'app/account-settings/models/user.model';
import { Language } from 'app/core/language.service';
import { CurrentContextService } from 'app/core/current-context.service';
import { SubscriptionHandlerBaseComponent } from 'app/core/subscription-handler.base';
import { EarlyAccessFeatures } from 'app/core/identity/user-identity';
import { UserService } from 'app/user/user.service';
import { IdentityService } from 'app/core/identity/identity.service';
import { AccountService } from 'app/account/account.service';
import { accountLanguages, AccountLanguage } from 'assets/languages/account-language';
import { BrandVariablesService } from '../../account/brand-variables.service';
import { SnackBarService } from '../../core/snack-bar.service';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { replaceUserField } from 'app/framework/user-patch';

@Component({
  selector: 'app-admin-settings-modal',
  templateUrl: './admin-settings-modal.component.html',
  styleUrls: ['./admin-settings-modal.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgTemplateOutlet,
    FormsModule,
    MatFormField,
    MatButton,
    MatProgressSpinner,
    TranslateModule,
    UpperCasePipe,
    MatSelectModule,
    MatInputModule,
    MatFormFieldModule,
  ],
})
export class AdminSettingsModalComponent extends SubscriptionHandlerBaseComponent implements OnInit {
  isVisible = false;
  settings: AdminSettings = {
    adminLanguage: null,
    adminTheme: null,
    adminToggle: false,
    adminAccountLanguage: null,
    aiFeatures: false,
    showServerSideCampaigns: false,
    cookieFeatures: false,
    versionHistory: false,
    undoRedo: false,
    appTheme: AppTheme.Theme2026BlueGold,
  };

  private BLANK_TEMPLATE_ID_DEV = 1271;
  private BLANK_TEMPLATE_ID_STAGING = 136;

  private currentAccount: Account;
  public currentUser: UserModel;
  public siteId: number;
  public isDevelopment = false;
  public isStaging = false;
  public isProduction: boolean = false;
  public environment = '';
  public languages = [
    { name: 'Reset', value: null },
    { name: 'English', value: Language.English },
    { name: 'Swedish', value: Language.Swedish },
  ];

  public adminAccountLanguages: AccountLanguage[] = accountLanguages.filter((l) => l.code !== '');

  public themes = [
    { name: 'Reset', value: null },
    { name: 'DeviceSetting', value: DarkMode.DeviceSettingsBased },
    { name: 'Light', value: DarkMode.AlwaysLight },
    { name: 'Dark', value: DarkMode.AlwaysDark },
  ];

  public appThemes = [
    { name: '2025-theme', value: AppTheme.Theme2025 },
    { name: '2026-theme-golden', value: AppTheme.Theme2026Golden },
    { name: '2026-theme-blue-gold', value: AppTheme.Theme2026BlueGold },
  ];
  public EarlyAccessFeaturesKeys = Object.keys(EarlyAccessFeatures);
  private userHasChanges = false;

  constructor(
    private settingsService: SettingsService,
    private campaignService: CampaignService,
    private readonly currentContextService: CurrentContextService,
    private readonly userService: UserService,
    private readonly identityService: IdentityService,
    private readonly router: Router,
    private readonly accountService: AccountService,
    private dialog: MatDialog,
    private brandVariablesService: BrandVariablesService,
    private snackbarService: SnackBarService,
    @Inject(ENVIRONMENT_DI) environment: Environment,
  ) {
    super();
    this.isDevelopment = environment.isDevelopment;
    this.isStaging = environment.isStaging;
    this.isProduction = environment.isProduction;
    if (this.isDevelopment) {
      this.environment = 'Development';
    } else if (this.isStaging) {
      this.environment = 'Staging';
    } else if (this.isProduction) {
      this.environment = 'Production';
    }
  }

  async ngOnInit(): Promise<void> {
    this.addSubscription(
      this.settingsService.settings$.subscribe((settings) => {
        this.settings = { ...settings };
      }),
    );

    this.addSubscription(
      this.currentContextService.currentContextBS.subscribe((context) => {
        if (!context) {
          return;
        }

        this.currentAccount = context.account;
        this.siteId = this.currentAccount.siteId;
        this.currentUser = context.user;

        this.settings.adminAccountLanguage = this.currentAccount.language ?? null;
      }),
    );
  }

  open(): void {
    this.isVisible = true;
    // Optionally, set focus to the modal for accessibility
    setTimeout(() => {
      const modal = document.querySelector('.modal-content') as HTMLElement;
      modal?.focus();
    }, 0);
  }

  close(): void {
    this.saveAccountLanguageIfChanged();
    this.settingsService.updateSettings(this.settings, true);
    this.isVisible = false;

    if (this.userHasChanges) {
      const earlyAccessFeatures = this.currentUser.earlyAccessFeatures;
      this.userService
        .patchCurrentUser([replaceUserField('earlyAccessFeatures', earlyAccessFeatures)])
        .subscribe(() => {
          this.userHasChanges = false;
          this.identityService.requestUpdateIdentityPayloadAsync();
          this.identityService.notifyPossibleIdentityUpdate();
          this.currentContextService.updateCurrentContextUser({
            ...this.currentContextService.currentContext.user,
            earlyAccessFeatures,
          });
        });
    }
  }

  private saveAccountLanguageIfChanged(): void {
    if (this.settings.adminAccountLanguage === (this.currentAccount.language ?? null)) {
      return;
    }
    this.accountService
      .patchCurrentAccountSettingsProperty([{ propertyName: 'language', value: this.settings.adminAccountLanguage }])
      .subscribe((updatedAccount) => {
        this.currentContextService.updateCurrentContextAccount(updatedAccount);
      });
  }

  isLoading: boolean = false;

  createBlankTemplate() {
    this.isLoading = true;
    const campaignType = OnsiteCampaignType.Regular;
    const blankTemplate = this.isDevelopment
      ? this.BLANK_TEMPLATE_ID_DEV
      : this.isStaging
        ? this.BLANK_TEMPLATE_ID_STAGING
        : undefined;

    if (!blankTemplate) {
      this.isLoading = false;
      return;
    }

    this.campaignService
      .createOnsiteCampaignFromTemplate(
        blankTemplate,
        campaignType,
        this.currentAccount.defaultFontFamily,
        undefined,
        undefined,
      )
      .subscribe(
        (campaign: OnsiteCampaign) => {
          this.router.navigate([`/campaigns/${campaign.id}/widgets/${campaign.widgets[0].id}/editor/`], {
            queryParams: { isNewWidget: true },
          });
          this.isLoading = false;
          this.close();
        },
        (error) => {
          this.isLoading = false;
        },
      );
  }

  resetBrandVariables() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      maxWidth: '600px',
      minWidth: '300px',
      panelClass: 'confirm-dialog-panel',
      data: {
        title: 'confirmationDialog.confirmationTitle',
        text: `Are you sure that you want to reset the brand settings for <strong>${this.currentAccount.name}</strong>?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.brandVariablesService.getDefaultSettings().subscribe((defaultBrandVariables) => {
          this.brandVariablesService.create(defaultBrandVariables).subscribe(() => {
            this.snackbarService.success('settings.customization.brand-settings.success-save');
          });
        });
      }
    });
  }

  toUserSetting() {
    this.router.navigate([`/backoffice/users`], {
      state: { navigateFromAdminMenu: true },
    });
    this.close();
  }

  public toggleFeature(feature: EarlyAccessFeatures, isChecked: boolean) {
    if (!this.currentUser?.earlyAccessFeatures) {
      return;
    }
    this.userHasChanges = true;

    if (isChecked) {
      // Add feature if not already present
      if (!this.currentUser.earlyAccessFeatures.includes(feature)) {
        this.currentUser.earlyAccessFeatures.push(feature);
      }
    } else {
      // Remove feature if present
      this.currentUser.earlyAccessFeatures = this.currentUser.earlyAccessFeatures.filter((f) => f !== feature);
    }
  }
}
